import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy, Medal, Shield, Layers, Calendar, Store, TrendingUp, Crown } from "lucide-react";
import { PlayerAvatar } from "../components/ranking/WeeklySpotlight";
import { formatDate } from "./NewsPage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Stat = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="glass-card rounded-2xl p-4 border border-purple-500/20" data-testid={testid}>
    <div className="flex items-center justify-between mb-1"><p className="text-[11px] tracking-widest text-gray-400 uppercase">{label}</p><Icon size={16} className={accent} /></div>
    <p className={`text-2xl md:text-3xl font-black ${accent}`}>{value ?? "-"}</p>
  </div>
);

const posStyle = (p) => (p === 1 ? "bg-gradient-to-r from-yellow-400 to-amber-500 text-black" : p === 2 ? "bg-gradient-to-r from-gray-300 to-gray-400 text-black" : p === 3 ? "bg-gradient-to-r from-amber-600 to-orange-700 text-white" : "bg-purple-900/60 text-gray-200");

export default function PlayerProfilePage() {
  const { name } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setP(null); setError(false);
    axios.get(`${API}/players/${encodeURIComponent(name)}/profile`).then((r) => setP(r.data)).catch(() => setError(true));
  }, [name]);

  if (error) return <div className="min-h-screen bg-black pt-32 text-center text-gray-400" data-testid="player-not-found">Jugador no encontrado. <Link to="/ranking" className="text-pink-400">Volver al ranking</Link></div>;
  if (!p) return <div className="min-h-screen bg-black pt-32 flex justify-center"><div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;

  const maxTotal = Math.max(1, ...p.temporadas.map((t) => t.total || 0));

  return (
    <div className="min-h-screen bg-black pt-24 pb-16" data-testid="player-profile-page">
      <div className="container mx-auto px-4">
        <Link to="/ranking" className="inline-flex items-center gap-2 text-pink-400 text-sm mb-6 hover:text-pink-300"><ArrowLeft size={16} /> Rankings</Link>

        <div className="relative rounded-3xl overflow-hidden border border-purple-500/30 glass-card p-6 md:p-8 mb-8">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-pink-500/15 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="relative self-start">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 blur-sm opacity-70" />
              <div className="relative"><PlayerAvatar player={{ Nombre: p.nombre, Photo: p.photo }} size="w-28 h-28 md:w-36 md:h-36" text="text-5xl" /></div>
              {p.all_time?.posicion <= 3 && <Crown size={30} className="absolute -top-4 left-1/2 -translate-x-1/2 text-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.8)]" />}
            </div>
            <div className="flex-1">
              <p className="text-xs tracking-[0.3em] text-yellow-400 font-bold mb-2">PERFIL DE DUELISTA</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-3" data-testid="player-name">{p.nombre}</h1>
              <div className="flex flex-wrap gap-2">
                {p.equipo && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-800/60 text-purple-200 text-sm font-semibold border border-purple-500/40" data-testid="player-team"><Shield size={14} /> {p.equipo}</span>}
                {p.all_time && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500/15 text-yellow-300 text-sm font-semibold border border-yellow-500/40" data-testid="player-alltime">#{p.all_time.posicion} All-Time · {p.all_time.total} pts</span>}
                {p.stats.mejor_posicion && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/15 text-cyan-300 text-sm font-semibold border border-cyan-500/40"><Medal size={14} /> Mejor: #{p.stats.mejor_posicion.posicion} ({p.stats.mejor_posicion.season})</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat label="Torneos jugados" value={p.stats.torneos_jugados} icon={Calendar} accent="text-cyan-300" testid="stat-torneos" />
          <Stat label="Torneos ganados" value={p.stats.torneos_ganados} icon={Trophy} accent="text-yellow-300" testid="stat-ganados" />
          <Stat label="Tops (Top 8)" value={p.stats.tops} icon={Medal} accent="text-pink-300" testid="stat-tops" />
          <Stat label="Pts por torneos" value={p.stats.puntos_torneos} icon={TrendingUp} accent="text-emerald-300" testid="stat-puntos" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <section className="glass-card rounded-2xl p-6 border border-purple-500/20" data-testid="player-seasons">
            <h2 className="text-base md:text-lg font-black text-yellow-400 mb-5 flex items-center gap-2"><TrendingUp size={18} /> Evolución por temporada</h2>
            {p.temporadas.length === 0 ? <p className="text-gray-400 text-sm">Sin datos de temporadas todavía.</p> : (
              <div className="space-y-4">
                {p.temporadas.map((t) => (
                  <div key={t.season_id}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-white font-semibold">{t.season}</span>
                      <span className="flex items-center gap-2"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${posStyle(t.posicion)}`}>#{t.posicion}</span><span className="text-yellow-300 font-black">{t.total} pts</span><span className="text-gray-500 text-xs">/ {t.jugadores} jug.</span></span>
                    </div>
                    <div className="h-2.5 rounded-full bg-black/50 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-yellow-400 transition-all duration-700" style={{ width: `${Math.max(4, (t.total / maxTotal) * 100)}%` }} /></div>
                    {Object.keys(t.detalle).length > 0 && <p className="text-[11px] text-gray-500 mt-1">{Object.entries(t.detalle).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="glass-card rounded-2xl p-6 border border-purple-500/20" data-testid="player-tournaments">
            <h2 className="text-base md:text-lg font-black text-yellow-400 mb-5 flex items-center gap-2"><Trophy size={18} /> Historial de torneos</h2>
            {p.torneos.length === 0 ? <p className="text-gray-400 text-sm">Aún no hay resultados de torneos registrados para este jugador.</p> : (
              <div className="divide-y divide-purple-900/40">
                {p.torneos.map((t) => (
                  <div key={t.result_id} className="flex items-center gap-3 py-3">
                    <span className={`w-10 h-10 rounded-full font-black text-sm flex items-center justify-center flex-shrink-0 ${posStyle(t.posicion)}`}>#{t.posicion}</span>
                    <div className="flex-1 min-w-0">
                      {t.torneo_id ? <Link to={`/torneos/${t.torneo_id}`} className="font-semibold text-white hover:text-yellow-300 truncate block">{t.torneo}</Link> : <p className="font-semibold text-white truncate">{t.torneo}</p>}
                      <p className="text-[11px] text-gray-400 flex flex-wrap gap-x-3"><span>{formatDate(t.fecha)}</span>{t.formato && <span>{t.formato}</span>}{t.deck && <span className="flex items-center gap-1"><Layers size={10} /> {t.deck}</span>}{t.tienda && <span className="flex items-center gap-1"><Store size={10} /> {t.tienda}</span>}</p>
                    </div>
                    <span className="text-emerald-300 font-black text-sm">+{t.puntos}</span>
                  </div>
                ))}
              </div>
            )}
            {p.stats.decks.length > 0 && <p className="text-xs text-gray-400 mt-4">Decks jugados: <span className="text-purple-200">{p.stats.decks.join(", ")}</span></p>}
          </section>
        </div>
      </div>
    </div>
  );
}
